import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { loadRemote, init, registerRemotes } from '@module-federation/runtime'
import { Helmet } from 'react-helmet'
import { normalizeMfeResult, deserializeSeoTags } from './MicroFrontendUtils'
import { ServiceErrorUI } from 'src/views/components/ServiceErrorUI'

// Installed version — keeps MF shared scope aligned with package.json / yarn.lock.
// Falls back to the last-known-good version if the bundler ever strips
// package.json from the module (e.g. sideEffects/production trimming)
// so this stays a soft failure instead of crashing at module load.
// Exported (pure, no React) so the fallback branch can be unit tested without
// needing to reset the module registry that 'react'/'react-dom' share.
export function resolveInstalledVersion(
  loadPackageJson,
  packageName,
  fallbackVersion
) {
  try {
    return loadPackageJson().version
  } catch (packageJsonError) {
    console.warn(
      `[RemoteLoader] Falling back to hardcoded version ${fallbackVersion} for ${packageName}:`,
      packageJsonError?.message
    )
    return fallbackVersion
  }
}

// Kept as a named export for existing spec coverage of the fallback branch.
export function resolvePubsubJsVersion() {
  return resolveInstalledVersion(
    () => require('pubsub-js/package.json'),
    'pubsub-js',
    '1.9.5'
  )
}

const PUBSUB_JS_VERSION = resolvePubsubJsVersion()
// mobx/mobx-react MUST be host-provided singletons: cart-mfe and checkout-mfe
// declare them as shared singletons and hydrate host-passed store snapshots.
// Without a host-provided instance each side runs its own MobX, cross-instance
// observable assignment silently fails, and the MFEs fall back to re-fetching
// data the host already has (redundant `carts` GET on every checkout mount).
const MOBX_VERSION = resolveInstalledVersion(
  () => require('mobx/package.json'),
  'mobx',
  '5.15.7'
)
const MOBX_REACT_VERSION = resolveInstalledVersion(
  () => require('mobx-react/package.json'),
  'mobx-react',
  '5.4.4'
)
const EMOTION_REACT_VERSION = resolveInstalledVersion(
  () => require('@emotion/react/package.json'),
  '@emotion/react',
  '11.14.0'
)
const EMOTION_STYLED_VERSION = resolveInstalledVersion(
  () => require('@emotion/styled/package.json'),
  '@emotion/styled',
  '11.14.1'
)

let sharedScopeInitPromise = null

// Remote modules resolved ahead of hydration, keyed by "<remote name>/<component>".
// App.js preloads the SSR'd MFEs before hydrateRoot() so RemoteComponent can
// render the real component on its very first render and hydrate the
// server-rendered MFE markup instead of replacing it.
const preloadedRemotes = new Map()
const preloadPromises = new Map()

export function remoteRequestKey(remoteConfig) {
  if (!(remoteConfig?.name && remoteConfig?.entry)) return null
  return `${remoteConfig.name}/${remoteConfig.component || 'App'}`
}

export function getPreloadedRemote(key) {
  return key ? preloadedRemotes.get(key) || null : null
}

export function preloadRemote(remoteConfig) {
  const key = remoteRequestKey(remoteConfig)
  if (!key) return Promise.resolve(null)
  if (preloadPromises.has(key)) return preloadPromises.get(key)

  const promise = ensureSharedScopeInitialized()
    .then(() => {
      registerRemotes([remoteConfig])
    })
    .then(() => loadRemote(key))
    .then(module => {
      if (module) preloadedRemotes.set(key, module)
      return module
    })
    .catch(error => {
      // Not fatal: RemoteLoader falls back to its own load path after mount.
      preloadPromises.delete(key)
      console.warn('[RemoteLoader] preloadRemote failed for', key, error?.message)
      return null
    })
  preloadPromises.set(key, promise)
  return promise
}

// Test hook — the preload cache is module state.
export function __resetPreloadedRemotes() {
  preloadedRemotes.clear()
  preloadPromises.clear()
}

// Eager-shared-module race guard (NGE1-2881):
function ensureSharedScopeInitialized() {
  if (!sharedScopeInitPromise) {
    sharedScopeInitPromise = Promise.resolve()
      .then(() =>
        init({
          name: 'equinoxStorefrontHost',
          shared: {
            react: {
              eager: true,
              version: '18.2.0',
              scope: 'default',
              lib: () => React,
              shareConfig: {
                singleton: true,
                requiredVersion: '^18.2.0',
              },
            },
            'react-dom': {
              eager: true,
              version: '18.2.0',
              scope: 'default',
              lib: () => ReactDOM,
              shareConfig: {
                singleton: true,
                requiredVersion: '^18.2.0',
              },
            },
            'pubsub-js': {
              eager: true,
              version: PUBSUB_JS_VERSION,
              scope: 'default',
              lib: () => require('pubsub-js'),
              shareConfig: {
                singleton: true,
                requiredVersion: `^${PUBSUB_JS_VERSION}`,
              },
            },
            mobx: {
              eager: true,
              version: MOBX_VERSION,
              scope: 'default',
              // Returns the host bundle's own mobx instance, so host stores
              // and remote stores share one MobX by construction.
              lib: () => require('mobx'),
              shareConfig: {
                singleton: true,
                requiredVersion: '^5.9.4',
              },
            },
            'mobx-react': {
              eager: true,
              version: MOBX_REACT_VERSION,
              scope: 'default',
              lib: () => require('mobx-react'),
              shareConfig: {
                singleton: true,
                requiredVersion: '^5.4.3',
              },
            },
            // cart-mfe declares emotion as a host-provided singleton; provide
            // the host instance so remotes reuse one emotion cache instead of
            // instantiating their own (duplicate style tags otherwise).
            '@emotion/react': {
              eager: true,
              version: EMOTION_REACT_VERSION,
              scope: 'default',
              lib: () => require('@emotion/react'),
              shareConfig: {
                singleton: true,
                requiredVersion: '^11.14.0',
              },
            },
            '@emotion/styled': {
              eager: true,
              version: EMOTION_STYLED_VERSION,
              scope: 'default',
              lib: () => require('@emotion/styled'),
              shareConfig: {
                singleton: true,
                requiredVersion: '^11.14.0',
              },
            },
          },
        })
      )
      .catch(initError => {
        // Allow a later mount to retry a genuinely failed first init.
        sharedScopeInitPromise = null
        throw initError
      })
  }
  return sharedScopeInitPromise
}

/**
 * Derives the initial isPageFound value from SSR routing data so the
 * component never flickers through a null state when SSR data is present.
 */
function deriveInitialPageFound(isServerDataAvailable, routing, legacyParams) {
  if (!isServerDataAvailable) return null
  if (routing) return routing.pageStatus !== 'not_found'
  return legacyParams?.isPageFound !== false
}

// componentParams  — MFE component payload (formerly the mixed `params` prop)
// routing          — host-layer routing signal ({ pageStatus, redirectionUrl, … })
//                    kept separate so MFE components never receive infrastructure keys
// seoTags          — serialized child-SSR descriptors from __initialData.
//                    Helmet fallback until live getSEOTags is loaded; after that
//                    getSEOTags({ params }) owns title/meta (including client nav).
// loaderId         — value of the data-mfe-loader-id attribute. RemoteLoader renders
//                    that container itself so the client's first render is the exact
//                    element the server emitted (hydration must not see an extra wrapper).
const RemoteLoader = ({
  remoteConfig,
  componentParams = {},
  routing = null,
  isServerDataAvailable = false,
  ssrFallbackHtml = '',
  seoTags,
  onPageNotFound,
  fallback = null,
  loaderId,
}) => {
  const component = remoteConfig?.component || 'App'

  // Preloaded (App.js, before hydrateRoot) → request key is known on first render.
  const [system, setSystem] = useState(() => {
    const key = remoteRequestKey(remoteConfig)
    return key && getPreloadedRemote(key) ? key : null
  })

  useEffect(() => {
    if (!(remoteConfig?.name && remoteConfig?.entry)) return

    const preloadKey = remoteRequestKey(remoteConfig)
    // The preload can resolve after the lazy initialiser above has already run
    // (hydration no longer waits for it). The remote is registered in that
    // case, so only the request key still has to be published — without this
    // `system` would stay null and the MFE would never mount.
    if (getPreloadedRemote(preloadKey)) {
      setSystem(preloadKey)
      return
    }

    let isCancelled = false

    ensureSharedScopeInitialized()
      .then(() => {
        try {
          // Adds just this instance's remote to the already-initialized host —
          // does not re-touch the shared scope, so no re-registration race.
          registerRemotes([remoteConfig])
        } catch (registerError) {
          console.warn(
            '[RemoteLoader] MF registerRemotes warning:',
            registerError?.message
          )
        }
      })
      .catch(initError => {
        console.warn('[RemoteLoader] MF init warning:', initError?.message)
      })
      .finally(() => {
        if (!isCancelled) setSystem(`${remoteConfig.name}/${component}`)
      })

    return () => {
      isCancelled = true
    }
  }, [remoteConfig?.name, remoteConfig?.entry])

  return (
    <RemoteComponent
      mfeRequest={system}
      componentParams={componentParams}
      routing={routing}
      isServerDataAvailable={isServerDataAvailable}
      ssrFallbackHtml={ssrFallbackHtml}
      seoTags={seoTags}
      onPageNotFound={onPageNotFound}
      fallback={fallback}
      loaderId={loaderId}
    />
  )
}

const RemoteComponent = ({
  mfeRequest,
  componentParams,
  routing,
  isServerDataAvailable,
  ssrFallbackHtml,
  seoTags,
  onPageNotFound,
  fallback = null,
  loaderId,
}) => {
  // When the module was preloaded and the server already ran
  // getServerSideProps, everything needed for the first render is at hand.
  const preloaded =
    mfeRequest && isServerDataAvailable ? getPreloadedRemote(mfeRequest) : null
  const preloadedComponent = preloaded ? preloaded.default || preloaded : null

  const [serverProps, setServerProps] = useState({})
  const [isReady, setIsReady] = useState(() => Boolean(preloadedComponent))
  const [isPageFound, setIsPageFound] = useState(() =>
    deriveInitialPageFound(isServerDataAvailable, routing, componentParams)
  )
  const [getSEOTags, setGetSEOTags] = useState(
    () => preloaded?.getSEOTags || null
  )
  const [Component, setComponent] = useState(() => preloadedComponent)
  let seoTagElements = []
  try {
    seoTagElements = deserializeSeoTags(seoTags)
  } catch (error) {
    console.error('[MFE Client] deserializeSeoTags failed', error)
  }

  const serializedSeoHelmet =
    seoTagElements.length > 0 ? <Helmet>{seoTagElements}</Helmet> : null

  useEffect(() => {
    let isCancelled = false

    if (!mfeRequest) {
      setComponent(null)
      setIsReady(false)
      return
    }

    // Handle SSR-provided redirect before loading the remote module.
    if (isServerDataAvailable) {
      const redirectUrl = routing?.redirectionUrl
      if (redirectUrl) {
        window.location.replace(redirectUrl)
        return
      }
      // The module may have been preloaded after this component first
      // rendered, in which case the lazy state initialisers above missed it.
      // Adopt it now rather than starting a second load; when it was already
      // adopted on the first render these setState calls bail out.
      const preloadedModule = getPreloadedRemote(mfeRequest)
      const PreloadedComp = preloadedModule
        ? preloadedModule.default || preloadedModule
        : null
      if (PreloadedComp) {
        setComponent(() => PreloadedComp)
        if (preloadedModule.getSEOTags) {
          setGetSEOTags(() => preloadedModule.getSEOTags)
        }
        setIsReady(true)
        return
      }
    }

    // Single unified load path for both SSR-hydration and CSR flows.
    // The only difference is whether getServerSideProps is called.
    // Retries once on the shared-scope race error (NGE1-2881) as a defensive
    // fallback — ensureSharedScopeInitialized() should already prevent it,
    // but a short retry costs little and covers any other timing edge case.
    const MAX_ATTEMPTS = 2
    const loadModule = async () => {
      let attempt = 0

      while (attempt < MAX_ATTEMPTS && !isCancelled) {
        try {
          const m = await loadRemote(mfeRequest)
          if (isCancelled) return

          const Comp = m?.default || m
          if (Comp) setComponent(() => Comp)
          if (m?.getSEOTags) setGetSEOTags(() => m.getSEOTags)

          if (!isServerDataAvailable) {
            if (m?.getServerSideProps) {
              const raw = await m.getServerSideProps({
                params: componentParams,
              })
              if (isCancelled) return
              // Normalise both new-envelope and legacy flat results into { _routing, props }.
              const result = normalizeMfeResult(raw)
              const redirectUrl = result?._routing?.redirectionUrl
              if (redirectUrl) {
                if (!isCancelled) window.location.replace(redirectUrl)
                return
              }
              const pageFound = result?._routing?.pageStatus !== 'not_found'
              setIsPageFound(pageFound)
              setServerProps(result?.props ?? {})
              if (!pageFound && onPageNotFound) onPageNotFound()
            } else {
              // No getServerSideProps — page is considered found.
              setIsPageFound(true)
            }
          }
          break
        } catch (err) {
          attempt += 1
          const isEagerSharedRace = /eager consumption/i.test(
            err?.message || ''
          )
          if (isEagerSharedRace && attempt < MAX_ATTEMPTS) {
            await new Promise(resolve => setTimeout(resolve, 150))
            continue
          }
          if (!isCancelled) {
            console.error('RemoteLoader: Error loading remote module', err)
            setIsPageFound(true) // Fail open — render MFE if the call errors.
          }
          break
        }
      }

      if (!isCancelled) setIsReady(true)
    }

    loadModule()
    return () => {
      isCancelled = true
    }
  }, [mfeRequest, isServerDataAvailable, onPageNotFound])

  // getServerSideProps explicitly said this page does not exist.
  if (isPageFound === false) return null

  const containerProps = loaderId ? { 'data-mfe-loader-id': loaderId } : {}

  // Keep SSR-rendered content visible while the client module is resolving.
  // The container carries the markup via dangerouslySetInnerHTML exactly like
  // the server did, so React hydrates it without inspecting the children.
  if (!mfeRequest || !isReady || !Component) {
    if (isServerDataAvailable && ssrFallbackHtml) {
      return (
        <>
          {serializedSeoHelmet}
          <div
            {...containerProps}
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: ssrFallbackHtml }}
          />
        </>
      )
    }
    // A load was attempted (mfeRequest set, loadModule() finished) but no
    // Component ever resolved
    if (mfeRequest && isReady) {
      return (
        <div {...containerProps}>
          <ServiceErrorUI />
        </div>
      )
    }
    // Host-supplied placeholder covers the pre-ready CSR window when there
    // is no SSR HTML to fall back to.
    return <div {...containerProps}>{fallback}</div>
  }

  return (
    <>
      {getSEOTags ? (
        <Helmet>
          {getSEOTags({
            params: componentParams,
            serverProps: isServerDataAvailable ? componentParams : serverProps,
            isServerDataAvailable,
          })}
        </Helmet>
      ) : (
        serializedSeoHelmet
      )}
      <div {...containerProps}>
        <Component
          params={{ ...componentParams, ...serverProps }}
          isServerDataAvailable={isServerDataAvailable}
        />
      </div>
    </>
  )
}

export default RemoteLoader
export { RemoteLoader }
